import { redactText } from '../../bootstrap/redact.js';
import { clusterAccessCheck, clusterStatusCheck } from './cluster.js';
import { customerStacksCheck } from './stacks.js';
import { burstSurchargeCheck, nodeFleetCheck } from './nodes.js';
import { dailyCostCheck, sizingLeversCheck } from './cost.js';
import { subnetCapacityCheck } from './network.js';
import { clusterAddonsCheck, clusterSupportCheck, databaseCheck, searchDomainCheck, } from './managed.js';
import { CHECK_OUTCOMES } from './model.js';
import { nodePoolLimitsCheck, pendingPodsCheck, requestsUsageCheck } from './workloads.js';
import { autoscalerThrashCheck, replicaRulesCheck, restartsCheck, workloadCreationCheck, } from './behaviour.js';
import { alertRoutingCheck, prometheusFitCheck } from './observability.js';
import { kongErrorsCheck, prometheusSeriesCheck, scrapeTargetsCheck } from './metrics.js';
import { controllerRewritesCheck, queueAdmissionCheck } from './controllers.js';
/**
 * Every standard check, in the order the tab lists a category's checks.
 *
 * Adding one is a definition here and fixtures in `registry.test.ts` — nothing
 * in the handler or the tab — which is the extension point IM-199 exists for.
 */
export const CHECKS = [
    clusterStatusCheck,
    clusterAccessCheck,
    nodeFleetCheck,
    subnetCapacityCheck,
    clusterSupportCheck,
    clusterAddonsCheck,
    nodePoolLimitsCheck,
    pendingPodsCheck,
    queueAdmissionCheck,
    requestsUsageCheck,
    workloadCreationCheck,
    restartsCheck,
    autoscalerThrashCheck,
    controllerRewritesCheck,
    replicaRulesCheck,
    alertRoutingCheck,
    prometheusFitCheck,
    prometheusSeriesCheck,
    scrapeTargetsCheck,
    kongErrorsCheck,
    databaseCheck,
    searchDomainCheck,
    burstSurchargeCheck,
    dailyCostCheck,
    sizingLeversCheck,
    customerStacksCheck,
];
/**
 * Bumped when a stored run's results would be misread by this build — a
 * renamed field, not a new check. A new check simply appears on the next run.
 */
export const CHECKS_VERSION = 1;
/**
 * Runs every check over one set of facts.
 *
 * Evidence values are passed through the redactor here, once, for the reason
 * the bundle gives (IM-54): a stack's status reason and a cluster's health
 * message are the cloud's own free text, read back through an API, and were
 * never through the customer's redactor. Doing it here rather than in each
 * check means a check written next year cannot forget.
 *
 * A check that throws is reported `unknown` with the error, rather than costing
 * the run every other check's answer.
 */
export const runChecks = (facts, checks = CHECKS) => checks.map((check) => {
    const head = { id: check.id, title: check.title, category: check.category };
    try {
        const evaluation = check.evaluate(facts);
        return {
            ...head,
            ...evaluation,
            evidence: evaluation.evidence.map((e) => ({ label: e.label, value: redactText(e.value) })),
        };
    }
    catch (error) {
        return {
            ...head,
            outcome: 'unknown',
            finding: 'This check could not be evaluated.',
            evidence: [
                { label: 'Error', value: redactText(error instanceof Error ? error.message : 'unknown') },
            ],
        };
    }
});
/** How many checks landed on each outcome, for a run's one-line summary. */
export const countOutcomes = (results) => {
    const counts = Object.fromEntries(CHECK_OUTCOMES.map((o) => [o, 0]));
    for (const result of results)
        counts[result.outcome] += 1;
    return counts;
};
//# sourceMappingURL=registry.js.map